<template>
  <v-col cols="12" lg="2" class="sticky-sidebar">
    <template v-if="!$vuetify.breakpoint.lgAndDown">
      <v-list
        class="pt-0 sticky-sidebar__item"
        :style="`${
          $vuetify.breakpoint.width < 1400 ? 'max-width: 163px' : null
        }`"
      >
        <active-hosting-plan-selector
          :items="services"
          :value="activeService"
          :returnObject="true"
          @change="changeService"
          itemText="name"
          itemValue="id"
          class="mb-6"
        />

        <v-subheader class="pl-0 p-4 mt-6 pb-3">{{
          $t("heading.hosting.sidebar.manage.title")
        }}</v-subheader>
        <v-list-item-group
          color="primary"
          class="d-flex flex-row flex-lg-column justify-start"
        >
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/summary`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{
                $t("heading.hosting.summary.title")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/domains`"
            v-if="
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_domains'
              )
            "
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{
                $t("heading.hosting.domains.title")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/ftp`"
            v-if="
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_ftp'
              )
            "
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{
                $t("heading.hosting.ftp.title")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/sftp`"
            v-if="
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_sftp'
              )
            "
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{
                $t(`heading.hosting.${sftpPageType}.title`)
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/databases`"
            v-if="
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_mysql'
              ) ||
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.phpmyadmin_sso'
              )
            "
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">
                {{ $t("heading.hosting.databases.title") }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_cron'
              )
            "
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/cron`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{
                $t("heading.hosting.cron.title")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_file_manager'
              )
            "
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/file-manager`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{
                $t("heading.hosting.fileManager.title")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="
              activeService.dns_server_enabled &&
              activeService.getUserPrivileges(
                activeService.privileges,
                'hosting.manage_dns'
              ) &&
              activeService.plan_settings.dns_management_enabled
            "
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/dns`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">
                {{ $t("heading.hosting.dns.title") }}</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>

        <v-subheader
          v-if="
            activeService.email_server_enabled &&
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_email'
            )
          "
          class="pl-0 p-4 mt-6 pb-3"
          >{{ $t("heading.hosting.sidebar.email.title") }}</v-subheader
        >
        <v-list-item-group
          v-if="
            activeService.email_server_enabled &&
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_email'
            )
          "
          color="primary"
          :active-class="'elevation-4--before'"
          class="d-flex flex-row flex-lg-column justify-start"
        >
          <v-list-item :ripple="false" :to="`/hosting/email-addresses`">
            <v-list-item-content>
              <v-list-item-title class="p-2">
                <span>{{ $t("heading.hosting.emailAddress.title") }}</span>
                <div
                  v-if="
                    activeService.showEmailRecordConfiguration &&
                    !activeService.isEmailRecordsConfigured
                  "
                  class="select-dot"
                />
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :ripple="false"
            :active-class="'elevation-4--before'"
            :to="`/hosting/email-forwarders`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">
                {{ $t("heading.hosting.emailForwarders.title") }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </template>
    <template v-else>
      <active-hosting-plan-selector
        :items="services"
        :value="activeService"
        :returnObject="true"
        @change="changeService"
        itemText="name"
        itemValue="id"
        class="mt-2"
      />
      <v-tabs
        :class="$vuetify.breakpoint.mobile && 'mt-4'"
        :style="$vuetify.breakpoint.lgAndDown && 'padding: 8px 8px!important;'"
        center-active
        class="pt-0 d-flex flex-row flex-lg-column justify-start"
        style="overflow: hidden"
      >
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/summary`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
        >
          <span class="p-2">{{ $t("heading.hosting.summary.title") }}</span>
        </v-tab>
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/domains`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
          v-if="
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_domains'
            )
          "
        >
          <span class="p-2">{{ $t("heading.hosting.domains.title") }}</span>
        </v-tab>
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/ftp`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
          v-if="
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_ftp'
            )
          "
        >
          <span class="p-2">{{ $t("heading.hosting.ftp.title") }}</span>
        </v-tab>
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/sftp`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
          v-if="
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_sftp'
            )
          "
        >
          <span class="p-2">{{
            $t(`heading.hosting.${sftpPageType}.title`)
          }}</span>
        </v-tab>
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/databases`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
          v-if="
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_mysql'
            ) ||
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.phpmyadmin_sso'
            )
          "
        >
          <span class="p-2">{{ $t("heading.hosting.databases.title") }}</span>
        </v-tab>
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/cron`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
          v-if="
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_cron'
            )
          "
        >
          <span class="p-2">{{ $t("heading.hosting.cron.title") }}</span>
        </v-tab>
        <v-tab
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/file-manager`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
          v-if="
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_file_manager'
            )
          "
        >
          <span class="p-2">{{ $t("heading.hosting.fileManager.title") }}</span>
        </v-tab>
        <v-tab
          v-if="
            activeService.dns_server_enabled &&
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_dns'
            ) &&
            activeService.plan_settings.dns_management_enabled
          "
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/dns`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
        >
          <span class="p-2">{{ $t("heading.hosting.dns.title") }}</span>
        </v-tab>
        <v-tab
          v-if="
            activeService.email_server_enabled &&
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_email'
            )
          "
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/email-addresses`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
        >
          <span class="p-2">{{
            $t("heading.hosting.emailAddress.title")
          }}</span>
          <div
            v-if="
              activeService.showEmailRecordConfiguration &&
              !activeService.isEmailRecordsConfigured
            "
            class="select-dot"
          />
        </v-tab>
        <v-tab
          v-if="
            activeService.email_server_enabled &&
            activeService.getUserPrivileges(
              activeService.privileges,
              'hosting.manage_email'
            )
          "
          :active-class="$vuetify.breakpoint.lgAndDown ? '' : 'elevation-2'"
          :ripple="false"
          :to="`/hosting/email-forwarders`"
          :style="$vuetify.breakpoint.lgAndDown ? 'min-width: unset!important;' : ''"
        >
          <span class="p-2">{{
            $t("heading.hosting.emailForwarders.title")
          }}</span>
        </v-tab>
      </v-tabs>
    </template>
  </v-col>
</template>

<script>
import ActiveHostingPlanSelector from "./ActiveHostingPlanSelector.vue";

export default {
  components: {
    ActiveHostingPlanSelector,
  },
  props: {
    services: {
      type: Array,
      default: () => {
        [];
      },
    },
    activeService: {
      type: Object,
    },
  },
  methods: {
    changeService: function (item) {
      this.$emit("update:activeService", item);
    },
  },
  computed: {
    sftpPageType() {
      if (
        this.activeService.plan_settings.default_ssh_access_enabled ||
        this.activeService.plan_settings.allow_user_enable_ssh_access
      ) {
        return "ssh";
      }
      return "sftp";
    },
  },
};
</script>

<style src="./HostingPagesSidebar.scss" lang="scss" scoped />
